/** Read-only snapshot. This module never modifies the existing application data. */
const exactKeys = new Set([
  'yumnote.recipes.v1', 'yumnote.lists.v1', 'yumnote.assistant.prototype.v3',
  'yumnote.goal-plan.v1', 'yumnote.flex-days.v1', 'yumnote.userFoods.v1',
  'yumnote.weight-history.v1', 'yumnote.workout.last', 'yumnote.glassMilliliters',
]);
const prefixes = ['yumnote.activity.v1.'];
export function isBackupKey(key: string): boolean { return exactKeys.has(key) || prefixes.some(prefix => key.startsWith(prefix)); }

export interface LocalBackup {
  format: 'yumnote-local-backup';
  version: 1;
  createdAt: string;
  localStorage: Record<string, string>;
  databases: Record<string, Record<string, unknown[]>>;
}

async function readExistingDatabase(name: string, excludeOutbox = false): Promise<Record<string, unknown[]> | null> {
  const db = await new Promise<IDBDatabase | null>((resolve, reject) => {
    let absent = false;
    const request = indexedDB.open(name);
    request.onupgradeneeded = () => { absent = true; request.transaction?.abort(); };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => absent ? resolve(null) : reject(request.error ?? new Error('Не удалось открыть локальную базу.'));
    request.onblocked = () => reject(new Error('Другая вкладка блокирует доступ к локальной базе.'));
  });
  if (!db) return null;
  try {
    const names = Array.from(db.objectStoreNames).filter(store => !(excludeOutbox && name === 'yumnote-web' && store === 'sync_operations'));
    if (!names.length) return {};
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(names, 'readonly');
      const result: Record<string, unknown[]> = Object.create(null) as Record<string, unknown[]>;
      for (const name of names) {
        const request = transaction.objectStore(name).getAll();
        request.onsuccess = () => { result[name] = request.result as unknown[]; };
      }
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error ?? new Error('Не удалось прочитать локальную базу.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Чтение локальной базы прервано.'));
    });
  } finally { db.close(); }
}
async function readSnapshot(excludeOutbox: boolean): Promise<LocalBackup> {
  const local: Record<string, string> = Object.create(null) as Record<string, string>;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isBackupKey(key)) { const value = localStorage.getItem(key); if (value !== null) local[key] = value; }
  }
  const databases: LocalBackup['databases'] = Object.create(null) as LocalBackup['databases'];
  for (const name of ['yumnote-web', 'yumnote-statistics']) {
    const data = await readExistingDatabase(name, excludeOutbox);
    if (data !== null) databases[name] = data;
  }
  return { format: 'yumnote-local-backup', version: 1, createdAt: new Date().toISOString(), localStorage: local, databases };
}

/** Full recovery snapshots retain the legacy outbox. */
export function makeLocalBackup(): Promise<LocalBackup> { return readSnapshot(false); }
/** The active synchronizer reads current records, not the unused legacy outbox. */
export function makeSyncSnapshot(): Promise<LocalBackup> { return readSnapshot(true); }
